import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { listOptions, useConfig } from '../context/ConfigContext';

// TS port of the source (Falah) app's pages/DonorForm.jsx — renamed to
// Member. Matches MemberController::validated() exactly: identity fields
// (first_name/last_name/email/mobile/address/gender/date_of_birth) are
// required/validated against the linked `user` (email uniqueness checked
// against the `users` table, not `members`), while member_since/
// member_type/notes are plain Member-table fields. `gender` here is
// `nullable|in:male,female` (no other value) matching the backend exactly.
interface MemberFormState {
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  address: string;
  gender: string;
  date_of_birth: string;
  member_since: string;
  member_type: string;
  notes: string;
}

const emptyForm: MemberFormState = {
  first_name: '',
  last_name: '',
  email: '',
  mobile: '',
  address: '',
  gender: '',
  date_of_birth: '',
  member_since: '',
  member_type: '',
  notes: '',
};

type FormErrors = Record<string, string[]>;

export function MemberForm() {
  const { lists } = useConfig();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState<MemberFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew || !id) return;
    api.get<Record<string, unknown>>(`/members/${id}`).then((res) => {
      setForm(
        Object.fromEntries(
          (Object.keys(emptyForm) as (keyof MemberFormState)[]).map((k) => [k, (res.data[k] as string) ?? ''])
        ) as unknown as MemberFormState
      );
      setLoaded(true);
    });
  }, [id, isNew]);

  const set =
    (key: keyof MemberFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
    );
    try {
      if (isNew) await api.post('/members', payload);
      else await api.put(`/members/${id}`, payload);
      navigate('/memberships');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <>
      <header className="page-header">
        <h1>{isNew ? 'Add member' : 'Edit member'}</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
          <label>
            First name *
            <input value={form.first_name} onChange={set('first_name')} required />
            {errors.first_name && <small className="error">{errors.first_name[0]}</small>}
          </label>
          <label>
            Last name *
            <input value={form.last_name} onChange={set('last_name')} required />
            {errors.last_name && <small className="error">{errors.last_name[0]}</small>}
          </label>
          <label>
            Email *
            <input type="email" value={form.email} onChange={set('email')} required />
            {errors.email && <small className="error">{errors.email[0]}</small>}
          </label>
          <label>
            Mobile
            <input value={form.mobile} onChange={set('mobile')} />
            {errors.mobile && <small className="error">{errors.mobile[0]}</small>}
          </label>
          <label className="span-2">
            Address
            <input value={form.address} onChange={set('address')} />
            {errors.address && <small className="error">{errors.address[0]}</small>}
          </label>
          <label>
            Gender
            <select value={form.gender} onChange={set('gender')}>
              <option value="">—</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            {errors.gender && <small className="error">{errors.gender[0]}</small>}
          </label>
          <label>
            Date of birth
            <input type="date" value={form.date_of_birth} onChange={set('date_of_birth')} />
            {errors.date_of_birth && <small className="error">{errors.date_of_birth[0]}</small>}
          </label>
          <label>
            Member since
            <input type="date" value={form.member_since} onChange={set('member_since')} />
            {errors.member_since && <small className="error">{errors.member_since[0]}</small>}
          </label>
          <label>
            Member type
            <select value={form.member_type} onChange={set('member_type')}>
              <option value="">—</option>
              {listOptions(lists, 'member_type').map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            {errors.member_type && <small className="error">{errors.member_type[0]}</small>}
          </label>
          <label className="span-2">
            Notes
            <textarea rows={2} value={form.notes} onChange={set('notes')} />
          </label>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/memberships">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
